import React, { useState, useEffect } from 'react';
import { X, Mail, User, Send, AlertCircle } from 'lucide-react';

interface TestEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (email: string, name: string) => Promise<void>;
  defaultEmail?: string;
  defaultName?: string;
}

export function TestEmailModal({ isOpen, onClose, onSend, defaultEmail = '', defaultName = '' }: TestEmailModalProps) {
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState(defaultName);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setEmail(defaultEmail);
      setName(defaultName);
      setError('');
    }
  }, [isOpen, defaultEmail, defaultName]);

  if (!isOpen) return null;

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSend = async () => {
    setError('');

    if (!email.trim()) {
      setError('Voer een email adres in');
      return;
    }

    if (!validateEmail(email)) {
      setError('Voer een geldig email adres in');
      return;
    }

    try {
      setSending(true);
      await onSend(email, name);
      onClose();
    } catch (err) {
      console.error('Error sending test email:', err);
      setError('Er ging iets mis bij het versturen van de test email');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !sending) {
      handleSend();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onKeyDown={handleKeyDown}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-[#231F20] flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#FF6B35]" />
            Test Email Versturen
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={sending}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Verstuur een test email om te zien hoe deze er bij de ontvanger uit ziet.
          </p>

          <div>
            <label htmlFor="test-email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Adres <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="test-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="naam@voorbeeld.nl"
                className="input pl-10"
                disabled={sending}
                autoFocus
              />
            </div>
          </div>

          <div>
            <label htmlFor="test-name" className="block text-sm font-medium text-gray-700 mb-2">
              Naam (optioneel)
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="test-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Voornaam of volledige naam"
                className="input pl-10"
                disabled={sending}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Deze naam wordt gebruikt voor de {'{{first_name}}'} placeholder
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={sending}
          >
            Annuleren
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !email}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Versturen...' : 'Verstuur Test Email'}
          </button>
        </div>
      </div>
    </div>
  );
}
